import { DocUploader } from "@portal/BaseComponents/Document/DocUpload/DocUploader";
import { Flex } from "@portal/BaseComponents/Flex/Flex";
import {
  Form,
  useForm,
  useFormWatch,
} from "@portal/BaseComponents/Form/AntForm";
import { StyledFormItem } from "@portal/BaseComponents/Form/style";
import { InvoiceSearchWrapper } from "@portal/BaseComponents/InvoicesSearch/InvoiceSearchWrapper";
import { onSelectedValuesChange } from "@portal/BaseComponents/InvoicesSearch/type";
import { Radio, RadioGroup } from "@portal/BaseComponents/Radio/AntRadio";
import { TypographyText } from "@portal/BaseComponents/Typography/AntTypography";
import { FormFooter } from "@portal/BaseComponents/UI/FormArea/FormFooter";
import { FormHeader } from "@portal/BaseComponents/UI/FormArea/FormHeader";
import { Icon } from "@portal/lib/icons/icons";
import {
  useCreateADocumentQuery,
  useDocumentTypesQuery,
} from "@portal/queries/document-query";
import {
  CreateDocument,
  CreateDocumentForm,
  DOCUMENT_MAPPING_TYPE,
  DocumentFieldIds,
} from "@portal/types/entities/documents";

import { Modal } from "@portal/BaseComponents/Modal/AntModal";
import { SkeletonInput } from "@portal/BaseComponents/Skeleton/AntSkeleton";
import useOpen from "@portal/hooks/useOpen";
import { useMemo } from "react";
import { normFile } from "../../RaiseADispute/DisputeForm/Modal/DisputeModal";
import {
  DocumentDetailsForm,
  DocumentTypesForm,
} from "../DocumentsFormComponents";
import {
  entityLevelOptions,
  filterDocumentTypesOptionPredicate,
} from "../predicate";

const DocIcon = <Icon icon={["fal", "file-plus"]} color="var(--cyan-6)" />;
interface AddDocumentModalProps {
  open: boolean;
  onClose: () => void;
  getUpdatedValues?: (value: CreateDocument) => void;
  value?: CreateDocument;
  onOpenChange?: (open: false) => void;
}
const containerStyles = {
  background: "var(--gray-2)",
  padding: "var(--space-20)",
  borderRadius: "var(--space-4)",
};
function AddDocumentModal(props: AddDocumentModalProps) {
  const { onClose, open } = props;
  const [form] = useForm<CreateDocumentForm>();

  const { open: disabled, handleOpenChange: handleDisable } = useOpen({
    open: false,
  });

  const createADocument = useCreateADocumentQuery();
  const documentEntity = useFormWatch<CreateDocument["entity_type"]>(
    DocumentFieldIds.entity_level,
    form
  );
  const invoiceType = useFormWatch<CreateDocument["mapping_type"]>(
    [DocumentFieldIds.mapping_type],
    form
  );
  const { data: documentTypes, isLoading } = useDocumentTypesQuery({
    entity_types: [documentEntity ?? "CUSTOMER"],
  });

  function getOptions() {
    return documentTypes?.list.filter(filterDocumentTypesOptionPredicate);
  }
  function getSelectedType() {
    return typesOptions
      ? typesOptions?.length > 1
        ? undefined
        : typesOptions?.map((f) => f.id).toString()
      : undefined;
  }

  const typesOptions = useMemo(getOptions, [documentTypes?.list]);
  const selectedType = useMemo(getSelectedType, [typesOptions]);
  function handleCreateADocument(value: CreateDocument) {
    createADocument.mutateAsync(value).then(() => {
      handleClose();
    });
  }
  function handleClose() {
    onClose();
  }
  function handleInvoiceSearch(...args: onSelectedValuesChange) {
    const [value] = args;
    form.setFieldValue([DocumentFieldIds.invoice_ids], value);
  }

  function handleSubmit() {
    form.validateFields().then((value) => {
      const uploadIds = value.file_upload_ids.map(
        (f) => f.response?.id
      ) as string[];
      handleCreateADocument({
        ...value,
        file_upload_ids: uploadIds,
        entity_type: undefined,
      });
    });
  }

  const HeaderTitle = (
    <Flex align="center" justify="start">
      {DocIcon}
      <TypographyText fs="20">Add Document(s)</TypographyText>
    </Flex>
  );
  const Footer = (
    <FormFooter
      cancelButtonProps={{
        onClick: handleClose,
      }}
      saveButtonProps={{
        onClick: handleSubmit,
        loading: createADocument.isLoading,
        disabled: createADocument.isLoading || disabled,
      }}
    />
  );
  const Header = (
    <FormHeader title={HeaderTitle} onClose={handleClose} mode="modal" />
  );

  const Invoices = (
    <StyledFormItem
      name={[DocumentFieldIds.invoice_ids]}
      style={{ width: "100%", paddingInlineStart: "24px" }}
      preserve={false}
      rules={[{ required: true, message: "This Field Required" }]}
    >
      <InvoiceSearchWrapper
        selectedValuesCallBack={handleInvoiceSearch}
        openInvoicesOnly
      />
    </StyledFormItem>
  );
  const EntityLevel = (
    <StyledFormItem
      label="Level"
      space="sp-1"
      initialValue="CUSTOMER"
      name={DocumentFieldIds.entity_level}
      style={{ width: "100%" }}
      preserve={false}
    >
      <RadioGroup
        options={entityLevelOptions}
        optionType="button"
        onChange={() => {
          form.setFieldValue(DocumentFieldIds.type_id, undefined);
        }}
      />
    </StyledFormItem>
  );
  const DocumentType = (
    <>
      {isLoading ? (
        <SkeletonInput active />
      ) : (
        <StyledFormItem
          label="Document Type"
          space="sp-1"
          name={DocumentFieldIds.type_id}
          style={{ width: "100%" }}
          preserve={false}
          rules={[{ required: true, message: "This is a required field." }]}
          initialValue={selectedType}
        >
          <DocumentTypesForm options={typesOptions} key={documentEntity} />
        </StyledFormItem>
      )}
    </>
  );
  const DocumentDetails = (
    <StyledFormItem
      label="Document Details"
      space="sp-1"
      name={DocumentFieldIds.description}
      style={{ width: "100%" }}
      preserve={false}
    >
      <DocumentDetailsForm />
    </StyledFormItem>
  );
  const Attachments = (
    <StyledFormItem
      label="Attachments"
      preserve={false}
      name={DocumentFieldIds.file_upload_ids}
      valuePropName="fileList"
      getValueFromEvent={normFile}
      rules={[{ required: true, message: "This is a required field." }]}
    >
      <DocUploader
        action="/api/v1/attachments"
        previewType="UPLOADED"
        onChange={(info) => {
          handleDisable(info.file.status === "uploading");
        }}
      />
    </StyledFormItem>
  );

  const AssociatedInvoices = (
    <StyledFormItem
      label="Associated Invoices"
      space="sp-1"
      name={[DocumentFieldIds.mapping_type]}
      style={{ width: "100%" }}
      preserve={false}
      initialValue="ALL"
    >
      <RadioGroup style={{ width: "100%", ...containerStyles }}>
        <Flex vertical space="12">
          <Radio<DOCUMENT_MAPPING_TYPE> value={"ALL"}>
            <TypographyText strong={invoiceType === "ALL"}>
              All Open Invoices
            </TypographyText>
          </Radio>
          <Radio<DOCUMENT_MAPPING_TYPE> value={"SELECTIVE"}>
            <TypographyText strong={invoiceType === "SELECTIVE"}>
              Selective Invoices
            </TypographyText>
          </Radio>
          {invoiceType === "SELECTIVE" ? Invoices : null}
        </Flex>
      </RadioGroup>
    </StyledFormItem>
  );

  return (
    <Modal
      open={open}
      width={640}
      onCancel={onClose}
      title={Header}
      footer={Footer}
      closable={false}
      centered
      destroyOnClose
      forceRender={false}
      styles={{
        body: {
          overflow: "auto",
          maxHeight: "600px",
        },
      }}
    >
      <Form form={form} layout="vertical" name="documents" preserve={false}>
        {EntityLevel}
        {DocumentType}
        {DocumentDetails}
        {documentEntity === "INVOICE" ? AssociatedInvoices : null}
        {Attachments}
      </Form>
    </Modal>
  );
}

export { AddDocumentModal };
